import React from "react";
import { compact, getDeltaColors, text } from "./dashboardPalette";

/**
 * Standardiziran header za sve dashboard kartice.
 *
 * Layout:
 * - Lijevo: title (slate-900 semibold) + subtitle (slate-500)
 * - Desno: opcionalni `right` slot (big number + Δ badge + sparkline + tooltip)
 *   ili kompletan `actions` slot (segmented control / filter)
 */
interface CardHeaderProps {
  title: string;
  subtitle?: React.ReactNode;
  right?: React.ReactNode;
  className?: string;
}

const CardHeader: React.FC<CardHeaderProps> = ({ title, subtitle, right, className = "mb-6" }) => {
  return (
    <div className={`flex items-end justify-between gap-4 ${className}`}>
      <div className="min-w-0">
        <span className={`${compact.title} font-semibold ${text.primary}`}>{title}</span>
        {subtitle && <div className={`${compact.subtitle} ${text.muted}`}>{subtitle}</div>}
      </div>
      {right && <div className="flex-shrink-0">{right}</div>}
    </div>
  );
};

export default CardHeader;

/**
 * Δ badge sa strelicom + obojenom pozadinom.
 *
 * - `value` u postocima (npr. 12.5 = "+12.5%")
 * - `inverted=true` za metrike gdje je "rast" loš (npr. troškovi, dani kašnjenja)
 * - `unit` za metrike koje su same postotak, pa se razlika mjeri u postotnim bodovima
 */
interface DeltaBadgeProps {
  value: number;
  inverted?: boolean;
  size?: "sm" | "md";
  showArrow?: boolean;
  decimals?: number;
  unit?: string;
}

export const DeltaBadge: React.FC<DeltaBadgeProps> = ({
  value,
  inverted = false,
  size = "sm",
  showArrow = true,
  decimals = 1,
  unit = "%",
}) => {
  const colors = getDeltaColors(value, inverted);
  const sizeClasses = size === "sm" ? "text-xs px-1.5 py-0.5" : "text-sm px-2 py-0.5";
  const sign = value > 0 ? "+" : "";
  return (
    <span className={`inline-flex items-center gap-0.5 rounded font-medium ${sizeClasses} ${colors.bg} ${colors.text}`}>
      {showArrow && colors.arrow !== "—" && <span aria-hidden>{colors.arrow}</span>}
      {sign}{value.toFixed(decimals)}{unit}
    </span>
  );
};

/**
 * Mali uppercase tag iznad big number-a (npr. "REVENUE" / "VOZILA").
 */
export const MetricLabel: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className = "",
}) => (
  <div className={`text-xs uppercase tracking-wide ${text.subtle} font-semibold ${className}`}>
    {children}
  </div>
);
